import React, { useState, useEffect } from "react";
import { AppCard } from "../../components/ui";
import { ExecutingScreenProps } from "./types";
import SkeletonLoader from "./SkeletonLoader";

const ExecutingScreen: React.FC<ExecutingScreenProps> = ({
  processingTransactions,
  executionProgress,
  onCancel
}) => {
  const [visibleLogs, setVisibleLogs] = useState<any[]>([]);
  const [showSkeleton, setShowSkeleton] = useState(true);

  useEffect(() => {
    const totalTransactions = processingTransactions.length;
    const progressRatio = executionProgress / 100;
    const shouldShowCount = Math.floor(totalTransactions * progressRatio);

    if (shouldShowCount > visibleLogs.length) {
      const timer = setTimeout(() => {
        setVisibleLogs(processingTransactions.slice(0, shouldShowCount));

        if (shouldShowCount >= totalTransactions) {
          setShowSkeleton(false);
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [executionProgress, processingTransactions, visibleLogs.length]);

  return (
    <AppCard>
      <AppCard.Header title="Processing Transactions" />
      <AppCard.Body>
        <div className="max-h-[calc(100vh-200px)] overflow-hidden">
          {/* Progress bar - only show if execution is not complete */}
          {executionProgress < 100 && (
            <div className="mb-6">
              <div className="mb-3">
                <div className="text-sm font-semibold text-gray-600 mb-2">
                  Execution Progress
                </div>
                <div className="h-1.5 bg-gray-200 rounded-sm overflow-hidden relative mb-2">
                  <div
                    className="progress-bar-fill h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-sm transition-all relative"
                    role="progressbar"
                    style={{ width: `${executionProgress}%` }}
                    aria-valuenow={executionProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  ></div>
                </div>
                <small className="mt-1 block text-xs font-medium text-gray-500">
                  {executionProgress}% complete
                </small>
              </div>
            </div>
          )}

          {/* Processing transactions table */}
          <div className="mb-5">
            <div className="max-h-[400px] overflow-y-auto border border-gray-300 rounded-md bg-white">
              <table className="w-full text-sm table-fixed">
                <thead className="bg-neutral-800 text-white sticky top-0 z-10">
                  <tr>
                    <th style={{ width: '33.33%' }} className="py-3 px-4 align-middle text-left">#</th>
                    <th style={{ width: '33.33%' }} className="py-3 px-4 align-middle text-center">Status</th>
                    <th style={{ width: '33.33%' }} className="py-3 px-4 align-middle text-right">Reference No</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLogs.length > 0 ? (
                    visibleLogs.map((transaction, index) => (
                      <tr
                        key={`${transaction.id}-${index}`}
                        className={`${transaction.status === 'Failed' ? 'bg-danger-50 dark:bg-danger-900/20' : 'even:bg-neutral-50 dark:even:bg-neutral-800/50'} animate-in`}
                        style={{ '--animation-delay': index } as React.CSSProperties}
                      >
                        <td style={{ width: '33.33%' }} className="py-3 px-4 align-middle text-left font-medium">{index + 1}</td>
                        <td style={{ width: '33.33%' }} className="py-3 px-4 align-middle text-center">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-sm font-bold ${
                              transaction.status === 'Success'
                                ? 'bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400'
                                : 'bg-danger-100 text-danger-800 dark:bg-danger-900/30 dark:text-danger-400'
                            }`}
                          >
                            {transaction.status}
                          </span>
                        </td>
                        <td style={{ width: '33.33%' }} className="py-3 px-4 align-middle text-right font-mono text-[13px]">
                          {transaction.referenceNo || '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3} className="text-center text-neutral-500 py-5 px-4">
                        {executionProgress === 0 ? 'Starting transaction processing...' :
                         executionProgress === 100 ? 'All transactions processed successfully!' :
                         'Processing transactions...'}
                      </td>
                    </tr>
                  )}

                  {/* Skeleton loader for remaining transactions */}
                  {showSkeleton &&
                   visibleLogs.length < processingTransactions.length &&
                   processingTransactions.length > 0 && (
                    <SkeletonLoader
                      count={Math.min(3, processingTransactions.length - visibleLogs.length)}
                      columns={3}
                      className="bg-gray-50 animate-pulse"
                    />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </AppCard.Body>
    </AppCard>
  );
};

export default ExecutingScreen;
